import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type {
    WorkOrderPriceData,
    ContractStatusEnum,
    ChapterEnum,
    WorkOrderPriceStatusEnum,
    WorkOrderActKc2Data,
    FilterValueOfWorkOrderPriceCodeFilterData,
} from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { badgePropsByStatus, CodeCell } from 'pages/Project/Common/CodeCell';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import { Loader } from 'uikit';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { WorkOrderRowMenu } from '../../WorkorderRowMenu';
import { CheckRowCell } from '../CheckRowCell';
import { WorkOrderPriceCodeCell } from '../WorkOrderPriceCodeCell';

import { destroyWorkOrderFilters, workOrderCodeFilterGroupHelper } from './helpers';
import {
    worKOrderCodeFilterGql,
    workOrderNameFilterGql,
    workOrderStatusFilterGql,
    workOrderUnitFilterGql,
} from './workOrderColumnFIlters';

interface IWorkOrderColumns {
    workOrderStatus: WorkOrderStatusEnum | undefined;
    workOrderId: number;
    workOrderSubObjectId: number;
    contractStatus: ContractStatusEnum | undefined;
    handleSetCheckedIds: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
    checkedIds: { [key: number]: boolean };
    isDisabledByProjectStatus: boolean;
    hasActs: boolean;
    isAllActKC2Accepted: boolean | undefined;
    chapter: ChapterEnum;
    seasonAndLinkedActsColumns: (ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[];
    typeWorkColumns?: ColDef<any>[];
    actsData: WorkOrderActKc2Data[];
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: { data: any } }) => void;
    gridApiRef: AgGridReact<any> | null;
    costRounding: number;
}

const getHeaderTitleTooltipCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость всего, руб. без НДС',
        childTooltip: 'Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

const getHeaderTitleTooltipUnitCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
        childTooltip: 'Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

export const useWorkOrderColumns = ({
    workOrderStatus,
    workOrderId,
    workOrderSubObjectId,
    contractStatus,
    handleSetCheckedIds,
    checkedIds,
    isDisabledByProjectStatus,
    hasActs,
    isAllActKC2Accepted,
    chapter,
    seasonAndLinkedActsColumns,
    typeWorkColumns,
    actsData,
    changeFilterHandler,
    gridApiRef,
    costRounding,
}: IWorkOrderColumns) => {
    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                handleSetCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [handleSetCheckedIds],
    );

    const filterVariables = useMemo(
        () => ({
            chapter,
            workOrderId,
            workOrderPriceObjectId: workOrderSubObjectId,
            order: { value: { uerCode: 'ASC' } },
        }),
        [chapter, workOrderId, workOrderSubObjectId],
    );

    const columns = useMemo<(ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 80,
                minWidth: 80,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: WorkOrderPriceData }) =>
                    !data ? (
                        <Loader />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CheckRowCell
                                contractStatus={contractStatus}
                                workOrderStatus={workOrderStatus}
                                onChange={handleChangeCheckbox(data.id)}
                                row={data}
                                checkedIds={checkedIds}
                                isDisabled={isDisabledByProjectStatus}
                                actsData={actsData}
                            />
                            <WorkOrderRowMenu
                                row={data}
                                contractStatus={contractStatus}
                                hasActs={hasActs}
                                workOrderStatus={workOrderStatus}
                                isAllAktKC2Accepted={isAllActKC2Accepted}
                                isDisabledByProjectStatus={isDisabledByProjectStatus}
                                actsData={actsData}
                                chapter={chapter}
                                buttonStyles={{ margin: '0 5px 2px', padding: '14px' }}
                            />
                        </div>
                    ),
            },
            {
                headerName: 'Статус',
                field: 'status',
                width: 110,
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: WorkOrderPriceData }) =>
                    !data ? '' : data?.status && <CodeCell status={data.status} />,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceStatusFilter',
                    query: workOrderStatusFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                value ? badgePropsByStatus[value].label : '',
                        },
                    ],
                    changeFilterHandler,
                },
                tooltipComponent: null,
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                cellClass: ({ data }) =>
                    data?.type === WorkOrderPriceTypeEnum.Uer ? 'textLeft' : 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: WorkOrderPriceData }) =>
                    !data ? '' : <WorkOrderPriceCodeCell row={data} workOrderStatus={workOrderStatus} />,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceCodeFilter',
                    query: worKOrderCodeFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['code'],
                    columns: [
                        { headerName: '', lockVisible: true, checkboxSelection: true, width: 50 },
                        {
                            headerName: 'Расценка',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceCodeFilterData }) =>
                                data?.value?.isUer ? data?.value?.uerCode : '',
                        },
                        {
                            headerName: 'МТР/Коэф.',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceCodeFilterData }) =>
                                !data?.value?.isUer ? data?.value?.code : '',
                        },
                    ],
                    changeFilterHandler,
                    customWhereVariablesGetter: (searchString: string) => ({
                        or: [
                            { value: { code: { containsi: searchString } } },
                            { value: { uerCode: { containsi: searchString } } },
                        ],
                    }),
                    groupAndSortItemsHelper: workOrderCodeFilterGroupHelper,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                width: 400,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceNameFilter',
                    query: workOrderNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: 'unit',
                width: 114,
                cellClass: 'centeredAgGridCell',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceUnitFilter',
                    query: workOrderUnitFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Ед. изм.',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                headerComponent: getHeaderTitleTooltipUnitCost,
                field: 'unitCost',
                width: 180,
                wrapHeaderText: true,
                cellClass: 'ag-right-aligned-cell',
                valueFormatter: getNumberFormatter(2),
            },
            {
                headerName: 'План по НЗ',
                headerClass: 'flexStart',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: 125,
                        cellClass: 'ag-right-aligned-cell boldCell',
                    },
                    {
                        headerComponent: getHeaderTitleTooltipCost,
                        valueFormatter: getNumberFormatter(costRounding),
                        field: 'totalPlanCost',
                        width: 160,
                        cellClass: 'ag-right-aligned-cell boldCell',
                    },
                ],
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Выполнено по нз',
                          headerClass: 'flexStart',
                          marryChildren: true,
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalCompletedVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalCompletedCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                      {
                          headerName: 'Остаток по нз',
                          marryChildren: true,
                          headerClass: 'flexStart',
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalRemainderVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalRemainderCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                  ]
                : []),
            ...(typeWorkColumns ? [...typeWorkColumns] : []),
            ...seasonAndLinkedActsColumns,
        ],
        [
            actsData,
            changeFilterHandler,
            chapter,
            checkedIds,
            contractStatus,
            costRounding,
            filterVariables,
            handleChangeCheckbox,
            hasActs,
            isAllActKC2Accepted,
            isDisabledByProjectStatus,
            seasonAndLinkedActsColumns,
            typeWorkColumns,
            workOrderStatus,
        ],
    );

    const [selection, , , setSelection] = useSelection('workOrderPriceTable');
    const [pinnedItems, , , setPinned] = usePin('workOrderPriceTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    ) as any[];

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    useEffect(() => {
        destroyWorkOrderFilters({
            api: gridApiRef?.api,
            columns: ['code', 'name', 'status', 'unit'],
        });
    }, [filterVariables, gridApiRef?.api]);

    return { fullSetColumns, fullSetSettingsColumns, handleTableSettingsConfirm, isColumnVisible };
};
